// @flow

import {catchError, debounceTime, filter, map, switchMap} from 'rxjs/operators';
import {of} from 'rxjs';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {ActionsObservable, ofType, StateObservable} from 'redux-observable';
import {arrayToOrderedMap, orderedMapToArray} from '../../utils/array';
import type {Action} from '../../store/utils';
import type {RootState} from '../index';
import type {ApiError} from '../../http/error';
import {
  getStorePointsApi,
  getStoreReferredsApi,
  getStoreInfoReferredsApi,
  redeemStoreSubscriptionApi,
  getRedeemedBenefitApi,
} from './api';

import {GetSessionSuccess, LoginSuccess} from '../auth/duck';
import {SaveSubscriptionSettingSuccess} from '../profile/account/duck';
import type {Referred, User, Pagination, CreationRequest} from '../../entities';

export type ReferralAction = Action & {
  error?: string,
  user?: User,
  queryFilters?: {[key: string]: string},
  //   data?: SaveAccountRequest,
};

export const SIZE = 6;
export type ReferralState = {
  isSaving?: boolean,
  isFetching?: boolean,
  isFetchingReferreds?: boolean,
  availablePoints?: number,
  referreds?: Referred[],
  errorPoints?: string,
  errorReferreds?: string,
  savingSuccess?: boolean,
  availablePoints?: boolean,
  referredsUpdated?: boolean,
  pagination?: Pagination,
  isRedeeming?: boolean,
  redeemError?: ApiError,
  redeemSuccess?: boolean,
  isFetchingCreationRequest?: boolean,
  creationRequest?: CreationRequest,
  creationRequestError?: string,
};

// export class FetchReferral extends ActionCreator<ReferralAction> {}

export class FetchStorePoints extends ActionCreator<ReferralAction> {}
export class FetchStorePointsSuccess extends ActionCreator<ReferralAction> {}
export class FetchStorePointsError extends ActionCreator<ReferralAction> {}

export class FetchStoreReferreds extends ActionCreator<ReferralAction> {}
export class FetchStoreReferredsSuccess extends ActionCreator<ReferralAction> {}
export class FetchStoreReferredsError extends ActionCreator<ReferralAction> {}

export class FetchStoreInfoReferreds extends ActionCreator<ReferralAction> {}
export class FetchStoreInfoReferredsSuccess extends ActionCreator<ReferralAction> {}
export class FetchStoreInfoReferredsError extends ActionCreator<ReferralAction> {}

export class RedeemStoreBenefit extends ActionCreator<ReferralAction> {}
export class RedeemStoreBenefitSuccess extends ActionCreator<ReferralAction> {}
export class RedeemStoreBenefitError extends ActionCreator<ReferralAction> {}

export class FetchCreationRequest extends ActionCreator<ReferralAction> {}
export class FetchCreationRequestSuccess extends ActionCreator<ReferralAction> {}
export class FetchCreationRequestError extends ActionCreator<ReferralAction> {}

export const actions = {
  fetchStorePoints: (payload: ReferralAction) => new FetchStorePoints(payload),
  fetchReferreds: (payload: ReferralAction) => new FetchStoreReferreds(payload),
  fetchReferredsInfo: (payload: ReferralAction) =>
    new FetchStoreInfoReferreds(payload),
  redeemStoreBenefit: (payload: ReferralAction) =>
    new RedeemStoreBenefit(payload),
  fetchCreationRequest: (payload: ReferralAction) =>
    new FetchCreationRequest(payload),
};

export const initialState = {
  isFetching: true,
  isFetchingReferreds: true,
};

export default createReducer(initialState, {
  [FetchStorePoints.type]: (state: ReferralState) => ({
    ...state,
    isFetching: true,
  }),
  [FetchStoreReferreds.type]: (state: ReferralState) => ({
    ...state,
    isFetchingReferreds: true,
  }),
  [FetchStoreInfoReferreds.type]: (state: ReferralState) => ({
    ...state,
    isFetchingReferreds: true,
  }),
  [FetchStorePointsSuccess.type]: (
    state: ReferralState,
    action: ReferralAction
  ) => ({
    ...state,
    isFetching: undefined,
    errorPoints: undefined,
    availablePoints: action.availablePoints,
  }),
  [FetchStoreReferredsSuccess.type]: (
    state: ReferralState,
    action: ReferralAction
  ) => ({
    ...state,
    isFetchingReferreds: undefined,
    referreds: action.data,
    pagination: action.pagination,
  }),
  [FetchStoreInfoReferredsSuccess.type]: (
    state: ReferralState,
    action: ReferralAction
  ) => {
    const oldReferreds = arrayToOrderedMap(state.referreds, 'refereeId');
    const newReferreds = arrayToOrderedMap(action.data, 'refereeId');
    const referreds = orderedMapToArray(oldReferreds.merge(newReferreds));
    return {
      ...state,
      referreds,
      isFetchingReferreds: undefined,
      errorReferreds: undefined,
      referredsUpdated: true,
    };
  },
  [FetchStorePointsError.type]: (
    state: ReferralState,
    action: ReferralAction
  ) => ({
    ...state,
    isFetching: false,
    errorPoints: action.error,
  }),
  [FetchStoreReferredsError.type]: (
    state: ReferralState,
    action: ReferralAction
  ) => ({
    ...state,
    isFetchingReferreds: false,
    referreds: [],
    errorReferreds: action.error,
  }),
  [FetchStoreInfoReferredsError.type]: (
    state: ReferralState,
    action: ReferralAction
  ) => ({
    ...state,
    isFetchingReferreds: false,
    referreds: [],
    errorReferreds: action.error,
  }),
  [RedeemStoreBenefit.type]: (
    state: ReferralState,
    action: ReferralAction
  ) => ({
    ...state,
    isRedeeming: true,
    redeemError: undefined,
    redeemSuccess: undefined,
  }),
  [RedeemStoreBenefitSuccess.type]: (
    state: ReferralState,
    action: ReferralAction
  ) => ({
    ...state,
    isRedeeming: undefined,
    redeemError: undefined,
    redeemSuccess: true,
  }),
  [RedeemStoreBenefitError.type]: (
    state: ReferralState,
    action: ReferralAction
  ) => ({
    ...state,
    isRedeeming: undefined,
    redeemSuccess: undefined,
    redeemError: action.redeemError,
  }),
  [FetchCreationRequest.type]: (
    state: ReferralState,
    action: ReferralAction
  ) => ({
    ...state,
    isFetchingCreationRequest: true,
  }),
  [FetchCreationRequestSuccess.type]: (
    state: ReferralState,
    action: ReferralAction
  ) => ({
    ...state,
    creationRequest: action.data,
    isFetchingCreationRequest: undefined,
  }),
  [FetchCreationRequestError.type]: (
    state: ReferralState,
    action: ReferralAction
  ) => ({
    ...state,
    creationRequest: null,
    isFetchingCreationRequest: undefined,
    redeemedBenefitError: action.error,
  }),
});

export function fetchStorePointEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(
      LoginSuccess.type,
      GetSessionSuccess.type,
      RedeemStoreBenefitSuccess.type
    ),
    debounceTime(500),
    switchMap(() => {
      return getStorePointsApi().pipe(
        map(
          ({availablePoints}: {availablePoints: number}) =>
            new FetchStorePointsSuccess({
              availablePoints,
            })
        ),
        buildCatchError(
          FetchStorePointsError,
          'Error al obtener puntos de la tienda'
        )
      );
    })
  );
}

export function fetchStoreReferredsEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(LoginSuccess.type, GetSessionSuccess.type, FetchStoreReferreds.type),
    debounceTime(500),
    switchMap(({queryFilters}: ReferralAction) => {
      const pagination = state$.value.referral.pagination;
      const qf = !pagination
        ? {...queryFilters, page: 0, size: SIZE}
        : {...queryFilters, page: pagination.page + 1, size: SIZE};
      return getStoreReferredsApi(qf).pipe(
        map(
          ({data, pagination}: {data: Referred[], pagination: Pagination}) =>
            new FetchStoreReferredsSuccess({
              data,
              pagination,
            })
        ),
        buildCatchError(
          FetchStoreReferredsError,
          'Error al obtener referidos de la tienda'
        )
      );
    })
  );
}

export function fetchStoreReferredsInfoEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchStoreReferredsSuccess.type),
    filter(({data}: {data: Referred[]}) => data.length > 0),
    debounceTime(500),
    switchMap(({data}: {data: Referred[]}) => {
      const arrStoreIds = data.map((ref: Referred) => ref.refereeId).join(',');
      if (arrStoreIds.length > 0) {
        return getStoreInfoReferredsApi({store_ids: arrStoreIds}).pipe(
          map((referreds: Referred[]) => {
            const newData = data.map(referred => {
              const findedReferred = referreds.find(
                ref => referred.refereeId === ref.storeId
              );
              return findedReferred ? {...referred, ...findedReferred} : {};
            });
            return new FetchStoreInfoReferredsSuccess({
              data: newData,
            });
          }),
          buildCatchError(
            FetchStoreInfoReferredsError,
            'Error al obtener referidos de la tienda'
          )
        );
      } else {
        return new FetchStoreInfoReferredsSuccess({
          data: [],
        });
      }
    })
  );
}

export function redeemStorePointsEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(RedeemStoreBenefit.type),
    debounceTime(500),
    switchMap(({benefit}) => {
      return redeemStoreSubscriptionApi({benefit}).pipe(
        map(() => {
          return new RedeemStoreBenefitSuccess();
        }),
        catchError((redeemError: ApiError) =>
          of(
            new RedeemStoreBenefitError({
              redeemError,
            })
          )
        )
      );
    })
  );
}

export function getRedeemedBenefitEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(
      LoginSuccess.type,
      GetSessionSuccess.type,
      SaveSubscriptionSettingSuccess.type,
      RedeemStoreBenefitSuccess.type,
      FetchCreationRequest.type
    ),
    switchMap(() => {
      return getRedeemedBenefitApi().pipe(
        map((data: CreationRequest) => new FetchCreationRequestSuccess({data})),
        buildCatchError(FetchCreationRequestError, 'Error')
      );
    })
  );
}
